import { useEffect, useMemo, useState } from 'react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { track } from '@/lib/analytics';
import { HARBOR_FACTS } from '@/lib/harbor-facts';
import {
  computeAllCosts,
  summarizeCosts,
  type AppCostResult,
  type CalculatorInput,
  type CostSummary,
} from '@/lib/pricing/compute';
import {
  buildHarborPricing,
  COMPETITOR_PRICING_APPS,
  formatUsd,
  midpoint,
} from '@/lib/pricing/models';
import { DEFAULT_NEED_IDS } from '@/lib/pricing/needs';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';
import { CostOverTimeChart } from './CostOverTimeChart';

const FIXED_YEARS = 5 as const;

const INPUT: CalculatorInput = {
  parents: 1,
  billing: 'annual',
  needs: [...DEFAULT_NEED_IDS],
  years: FIXED_YEARS,
  onlyIPay: false,
};

function buildAnalyticsProps(input: CalculatorInput, summary: CostSummary) {
  return {
    parents: input.parents,
    billing: input.billing,
    needs_count: input.needs.length,
    years: input.years,
    cheapest_app: summary.cheapestCovering?.app.slug ?? 'none',
    delta_vs_harbor:
      summary.harborDelta === null ? null : Math.round(summary.harborDelta),
  };
}

function comparisonCopy(
  harbor: AppCostResult | null,
  competitor: AppCostResult | null,
  years: number,
  parents: 1 | 2
) {
  if (!harbor?.annual || !competitor?.annual) {
    return 'No modeled comparison is available for this app.';
  }

  const harborTotal = midpoint(harbor.annual) * years;
  const competitorTotal = midpoint(competitor.annual) * years;
  const difference = competitorTotal - harborTotal;
  const rounded = Math.round(Math.abs(difference));
  const parentLabel = parents === 1 ? 'one parent' : 'two parents';

  if (rounded < 1) {
    return `Harbor and ${competitor.app.name} cost about the same over ${years} years for ${parentLabel}.`;
  }

  if (difference > 0) {
    return (
      <>
        Harbor is about{' '}
        <span className='text-emerald-600'>
          {formatUsd(rounded)} cheaper
        </span>{' '}
        than {competitor.app.name} over {years} years for {parentLabel}.
      </>
    );
  }

  return `Harbor is about ${formatUsd(rounded)} more expensive than ${competitor.app.name} over ${years} years for ${parentLabel}.`;
}

export function AppCostCalculator() {
  const input = INPUT;

  const harborPricing = useMemo(
    () =>
      buildHarborPricing(
        Number(HARBOR_FACTS.monthlyPrice),
        Number(HARBOR_FACTS.annualPrice),
        `${HARBOR_FACTS.siteUrl}/purchase`,
        '2026-10-30'
      ),
    []
  );
  const apps = useMemo(() => [harborPricing, ...COMPETITOR_PRICING_APPS], [harborPricing]);
  const results = useMemo(() => computeAllCosts(apps, input), [apps, input]);
  const chartResults = useMemo(
    () => computeAllCosts(apps, { ...input, billing: 'annual' }),
    [apps, input]
  );
  const competitors = chartResults.filter(
    (result) => result.app.slug !== 'harbor' && result.covered && result.annual
  );
  const [compareSlug, setCompareSlug] = useState(competitors[0]?.app.slug ?? '');
  const compare = competitors.some((result) => result.app.slug === compareSlug)
    ? compareSlug
    : (competitors[0]?.app.slug ?? '');
  const summary = useMemo(() => summarizeCosts(results, input), [results, input]);
  const chartHarbor =
    chartResults.find((result) => result.app.slug === 'harbor') ?? null;
  const chartCompare =
    chartResults.find((result) => result.app.slug === compare) ?? null;
  const comparison = comparisonCopy(
    chartHarbor,
    chartCompare,
    input.years,
    input.parents
  );
  const utm = getUtmParamsFromUrl();
  const analyticsProps = buildAnalyticsProps(input, summary);

  useEffect(() => {
    track('App Cost Calculator View');
  }, []);

  return (
    <div className='space-y-8'>
      <CostOverTimeChart
        results={chartResults}
        years={input.years}
        compare={compare}
        onCompareChange={setCompareSlug}
      />

      <h2
        aria-live='polite'
        aria-atomic='true'
        className='text-center text-2xl font-bold text-gray-900'
      >
        {comparison}
      </h2>

      <div className='mx-auto w-full max-w-xl print:hidden'>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <a
            href={getAppStoreUrl('app-cost-calculator', utm)}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() =>
              track('App Cost Calculator Harbor CTA Clicked', {
                ...analyticsProps,
                source: 'app_store',
              })
            }
            className='inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover'
          >
            <FaApple size={18} />
            <span>App Store</span>
          </a>
          <a
            href={getPlayStoreUrl('app-cost-calculator', utm)}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() =>
              track('App Cost Calculator Harbor CTA Clicked', {
                ...analyticsProps,
                source: 'google_play',
              })
            }
            className='inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover'
          >
            <FaGooglePlay size={18} />
            <span>Google Play</span>
          </a>
        </div>
      </div>
    </div>
  );
}
